import type { GameState, Locale } from "../domain/types";

const localeTags: Record<Locale, string> = { ru: "ru-RU", en: "en-US" };

export function formatAmount(value: string, locale: Locale, compact = false): string {
  const numeric = BigInt(value);
  return new Intl.NumberFormat(localeTags[locale], {
    notation: compact && (numeric >= 1_000_000n || numeric <= -1_000_000n) ? "compact" : "standard",
    maximumFractionDigits: compact ? 1 : 0,
  }).format(numeric);
}

export function formatCurrencyUnit(value: string, locale: Locale): string {
  const numeric = BigInt(value);
  if (locale === "en") return numeric === 1n ? "carat" : "carats";
  const absolute = numeric < 0n ? -numeric : numeric;
  const lastTwo = Number(absolute % 100n);
  const last = Number(absolute % 10n);
  if (lastTwo >= 11 && lastTwo <= 14) return "карат";
  if (last === 1) return "карат";
  if (last >= 2 && last <= 4) return "карата";
  return "карат";
}

export function formatCurrencyAmount(value: string, locale: Locale, compact = false): string {
  return `${formatAmount(value, locale, compact)} ${formatCurrencyUnit(value, locale)}`;
}

export function formatPassiveRate(perHour: string, locale: Locale): string {
  const rate = BigInt(perHour);
  const wholeHundredths = (rate * 100n + 1_800n) / 3_600n;
  const numeric = Number(wholeHundredths) / 100;
  const formatted = new Intl.NumberFormat(localeTags[locale], {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(numeric);
  return locale === "ru" ? `${formatted}/с` : `${formatted}/s`;
}

export function formatProductionSpeed(perHour: string, locale: Locale): string {
  const compactRate = formatPassiveRate(perHour, locale);
  const value = compactRate.replace(locale === "ru" ? "/с" : "/s", "");
  return locale === "ru" ? `${value} карат/с` : `${value} carats/s`;
}

function formatEnergyRateValue(rate: number, locale: Locale): string {
  return new Intl.NumberFormat(localeTags[locale], {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  }).format(rate);
}

export function formatEnergyRecoveryRate(secondsPerUnit: string | number, locale: Locale): string {
  const seconds = Number(secondsPerUnit);
  const rate = Number.isFinite(seconds) && seconds > 0 ? 1 / seconds : 0;
  return formatEnergyRateValue(rate, locale);
}

export function formatEnergyRecoveryGain(
  currentSecondsPerUnit: string | number,
  nextSecondsPerUnit: string | number,
  locale: Locale,
): string {
  const currentSeconds = Number(currentSecondsPerUnit);
  const nextSeconds = Number(nextSecondsPerUnit);
  const currentRate = Number.isFinite(currentSeconds) && currentSeconds > 0 ? 1 / currentSeconds : 0;
  const nextRate = Number.isFinite(nextSeconds) && nextSeconds > 0 ? 1 / nextSeconds : 0;
  return formatEnergyRateValue(Math.max(0, nextRate - currentRate), locale);
}

export function formatOfflineDuration(totalSeconds: number, locale: Locale): string {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(seconds / 3_600);
  const minutes = Math.floor((seconds % 3_600) / 60);
  const remainingSeconds = seconds % 60;

  if (locale === "en") {
    const units: string[] = [];
    if (hours > 0) units.push(`${hours} ${hours === 1 ? "hour" : "hours"}`);
    if (minutes > 0) units.push(`${minutes} ${minutes === 1 ? "minute" : "minutes"}`);
    if (hours === 0 && remainingSeconds > 0) {
      units.push(`${remainingSeconds} ${remainingSeconds === 1 ? "second" : "seconds"}`);
    }
    return units.join(" ") || "less than a second";
  }

  const word = (value: number, one: string, few: string, many: string): string => {
    const lastTwo = value % 100;
    const last = value % 10;
    if (lastTwo >= 11 && lastTwo <= 14) return many;
    if (last === 1) return one;
    if (last >= 2 && last <= 4) return few;
    return many;
  };
  const units: string[] = [];
  if (hours > 0) units.push(`${hours} ${word(hours, "час", "часа", "часов")}`);
  if (minutes > 0) units.push(`${minutes} ${word(minutes, "минута", "минуты", "минут")}`);
  if (hours === 0 && remainingSeconds > 0) {
    units.push(`${remainingSeconds} ${word(remainingSeconds, "секунда", "секунды", "секунд")}`);
  }
  return units.join(" ") || "меньше секунды";
}

export function formatServerDuration(endAt: string, serverNow: string, locale: Locale): string {
  const seconds = Math.max(0, Math.floor((new Date(endAt).getTime() - new Date(serverNow).getTime()) / 1000));
  const days = Math.floor(seconds / 86_400);
  const hours = Math.floor((seconds % 86_400) / 3_600);
  const minutes = Math.floor((seconds % 3_600) / 60);

  if (locale === "ru") {
    const word = (value: number, one: string, few: string, many: string): string => {
      const lastTwo = value % 100;
      const last = value % 10;
      if (lastTwo >= 11 && lastTwo <= 14) return many;
      if (last === 1) return one;
      if (last >= 2 && last <= 4) return few;
      return many;
    };
    if (days > 0) {
      const dayLabel = `${days} ${word(days, "день", "дня", "дней")}`;
      return hours > 0 ? `${dayLabel} ${hours} ${word(hours, "час", "часа", "часов")}` : dayLabel;
    }
    const hourLabel = `${hours} ${word(hours, "час", "часа", "часов")}`;
    return minutes > 0 ? `${hourLabel} ${minutes} ${word(minutes, "минута", "минуты", "минут")}` : hourLabel;
  }
  if (days > 0) {
    const dayLabel = `${days} ${days === 1 ? "day" : "days"}`;
    return hours > 0 ? `${dayLabel} ${hours} ${hours === 1 ? "hour" : "hours"}` : dayLabel;
  }
  const hourLabel = `${hours} ${hours === 1 ? "hour" : "hours"}`;
  return minutes > 0 ? `${hourLabel} ${minutes} ${minutes === 1 ? "minute" : "minutes"}` : hourLabel;
}

export type SeasonCountdownParts = {
  days: number;
  hours: number;
  minutes: number;
};

type SeasonCountdownSource = Pick<
  GameState["season"],
  "status" | "startsAt" | "endsAt" | "claimClosesAt"
>;

export function getSeasonCountdownTarget(season: SeasonCountdownSource): string {
  if (season.status === "scheduled") return season.startsAt;
  if (season.status === "claimable" && season.claimClosesAt !== null) return season.claimClosesAt;
  return season.endsAt;
}

export function getSeasonCountdownParts(endAt: string, serverNow: string): SeasonCountdownParts {
  const seconds = Math.max(0, Math.floor((new Date(endAt).getTime() - new Date(serverNow).getTime()) / 1_000));
  return {
    days: Math.floor(seconds / 86_400),
    hours: Math.floor((seconds % 86_400) / 3_600),
    minutes: Math.floor((seconds % 3_600) / 60),
  };
}

export function formatSeasonCountdown(endAt: string, serverNow: string, locale: Locale): string {
  const { days, hours, minutes } = getSeasonCountdownParts(endAt, serverNow);
  if (locale === "en") return `${days}d ${hours}h ${minutes}m`;
  return `${days} дн. ${hours} ч. ${minutes} мин.`;
}

export function formatUsdtCents(value: string, locale: Locale, alwaysShowCents = false): string {
  const cents = BigInt(value);
  const numeric = Number(cents) / 100;
  const formatted = new Intl.NumberFormat(localeTags[locale], {
    minimumFractionDigits: alwaysShowCents ? 2 : cents % 100n === 0n ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(numeric);
  return `${formatted} USDT`;
}

export function formatUsdtMicros(value: string, locale: Locale, maximumFractionDigits = 2): string {
  const micros = BigInt(value);
  const numeric = Number(micros) / 1_000_000;
  const formatted = new Intl.NumberFormat(localeTags[locale], {
    minimumFractionDigits: Math.min(2, maximumFractionDigits),
    maximumFractionDigits,
  }).format(numeric);
  return `${formatted} USDT`;
}

export function formatSharePpm(value: string, locale: Locale): string {
  const ppm = BigInt(value);
  if (ppm === 0n) return "0%";
  if (ppm > 0n && ppm < 10n) return "<0,001%".replace(",", locale === "ru" ? "," : ".");
  return `${new Intl.NumberFormat(localeTags[locale], {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  }).format(Number(ppm) / 10_000)}%`;
}

export function formatServerDate(value: string, locale: Locale): string {
  return new Intl.DateTimeFormat(localeTags[locale], {
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "UTC",
    timeZoneName: "short",
  }).format(new Date(value));
}

export const formatSeasonTime = formatServerDuration;
export const formatUtcDate = formatServerDate;

export function missingAmount(cost: string, available: string): string {
  const difference = BigInt(cost) - BigInt(available);
  return (difference > 0n ? difference : 0n).toString();
}
